import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { Col, Form as FormAntd, Row, Table, Typography, message } from 'antd';
import { DeleteOutlined, MinusOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { ActionButton } from '@hdi-seguros/web-components.action-button';
import {
  ColSize,
  ContentSection,
  RowSize,
} from '../../../../../../../../../shared/components';
import {
  DatePickerForm,
  Form,
  InputNumberForm,
  SelectForm,
} from '../../../../../../../../../shared/components/Form';
import { Modal } from '@hdi-seguros/web-components.modal';
import { QuotationStepButtons } from '../../../../../shared';

import { QuotationInfoContext } from '../../../../../shared/contexts';
import { StepThreeFormContext } from '../../../StepThree/contexts/StepThreeForm';
import {
  StepTwoFormContext,
  TableActivityItem,
} from '../../contexts/StepTwoForm';
import { WizardDataFormContext } from '../../../../Wizard/contexts/WizardDataForm';

import {
  APActivitiesPerProduct,
  Activity,
} from '../../../../../../../../../shared/interfaces';
import { QuotationStep } from '../../../../../shared/interfaces';

import { getExplanatoryTexts, StepTwoForm } from '../../../../../utils';
import {
  EMPTY_TEXT,
  ValidateForm,
} from '../../../../../../../../../shared/utils';

import { MasterContext } from '../../../../../../../../../shared/contexts';
import { COLORS } from '../../../../../../../../../config/themes/colors';

interface ActiviesForm {
  key: string;
  activity: string;
  quantity: string;
  ageRange: string;
  relationship: string;
}

export const PropertyDataAP = () => {
  const validateForm = new ValidateForm();

  const { setCurrentStep, quotationInfo } = useContext(QuotationInfoContext);

  const {
    ageRangeList,
    relationshipList,
    setQuotationForm,
    getAgeRangeDescriptionById,
    getRelationshipDescriptionById,
    getPaymentMethodPerVigency,
  } = useContext(WizardDataFormContext);

  const {
    stepTwoForm,
    setStepTwoForm,
    productsList,
    isLoadingProductsList,
    apActiviesList,
    apActiviesListOptions,
    loadActivitiesTableFormInfo,
    isLoadingProductsInfo,
    activities,
    setActivities,
    productsSelectedHistory,
    checkProductChange,
    showOnChangeProductModal,
    setShowOnChangeProductModal,
    setPlanWaschanged,
    propertyDataForm,
    apFormValues,
    setApFormValues,
    getActivityDescriptionById,
  } = useContext(StepTwoFormContext);

  const { setActiveTab } = useContext(StepThreeFormContext);

  const [activitiesTableForm] = FormAntd.useForm<ActiviesForm>();

  const productSelected: string = useMemo(
    () => propertyDataForm?.getFieldValue('plans') || stepTwoForm?.plans,
    /* eslint-disable-next-line */
    [propertyDataForm?.getFieldValue('plans'), stepTwoForm?.plans]
  );
  
  const activitySelected: string = useMemo(
    () => propertyDataForm?.getFieldValue('activity') || stepTwoForm?.activity,
    /* eslint-disable-next-line */
    [propertyDataForm?.getFieldValue('activity'), stepTwoForm?.activity]
  );

  const activitiesSelected: Array<TableActivityItem> = useMemo(
    () => (activities.length ? activities : []),
    /* eslint-disable-next-line */
    [activities]
  );

  const onDeleteActivity = useCallback(
    (key: string) => {
      const filteredActivities = activities.filter(
        (activity) => activity.key !== key
      );
      setActiveTab(filteredActivities[0]?.activity);
      setActivities(filteredActivities);
    },
    [activities, setActivities]
  );

  const MOCK_TABLE_COLUMNS_HEADER = useMemo(
    () => [
      {
        title: 'Actividad',
        dataIndex: 'activity',
        render: (id: string) => getActivityDescriptionById(id) ?? EMPTY_TEXT,
      },
      {
        title: 'Cantidad',
        dataIndex: 'quantity',
      },
      {
        title: 'Rango de Edad',
        dataIndex: 'ageRange',
        render: (id: string) => getAgeRangeDescriptionById(id) ?? EMPTY_TEXT,
      },
      {
        title: 'Parentesco',
        dataIndex: 'relationship',
        render: (id: string) =>
          getRelationshipDescriptionById(id) ?? EMPTY_TEXT,
      },
      {
        title: 'Acción',
        dataIndex: 'action',
        className: 'action-column',
        render: (_: any, data: TableActivityItem) => (
          <ActionButton
            label=''
            icon={<DeleteOutlined />}
            color='danger'
            type='secondary'
            onClick={() => onDeleteActivity(data.key)}
          />
        ),
      },
    ],
    [
      onDeleteActivity,
      getActivityDescriptionById,
      getAgeRangeDescriptionById,
      getRelationshipDescriptionById,
    ]
  );

  const addNewActivity = (formFields: ActiviesForm) => {
    const key = String(Math.random());

    const newActivity: TableActivityItem = {
      key,
      activityDescription: getActivityDescriptionById(formFields.activity),
      activity: formFields.activity,
      ageRange: formFields.ageRange,
      quantity: formFields.quantity,
      relationship: formFields.relationship,
    };

    setActivities((prevState: Array<TableActivityItem>) => [
      ...prevState,
      newActivity,
    ]);

    activitiesTableForm.resetFields();
  };

  const updateActivityQuantity = useCallback(
    (activityIndex: number, quantity: string) => {
      const updatedActivities = activities.map((activity, index) => {
        if (index === activityIndex) {
          activity['quantity'] = activity['quantity'] + quantity;
        }
        return activity;
      });

      setActivities(updatedActivities);

      activitiesTableForm.resetFields();
    },
    [activities, setActivities, activitiesTableForm]
  );

  const onPressAddActivityButton = useCallback(
    (formFields: ActiviesForm) => {
      if (!activities.length) return addNewActivity(formFields);

      const activityRepeatedIndex = activities.findIndex(
        (el) =>
          el.activity === formFields.activity &&
          el.ageRange === formFields.ageRange &&
          el.relationship === formFields.relationship
      );


      const newQuantity = Number(formFields.quantity);

      if (newQuantity > 99) {
        return message.error('La cantidad de personas debe ser entre 1 y 99');
      }
      
      if (activityRepeatedIndex !== -1) {
        const totalQuantity = Number(activities[activityRepeatedIndex].quantity) + newQuantity;

        if (totalQuantity > 99) {
          return message.error('La cantidad de personas debe ser entre 1 y 99');
        }
        
        return updateActivityQuantity(
          activityRepeatedIndex,
          formFields.quantity
        );
      }

      if (activities.length === 6) {
        return message.error('Como máximo se pueden agregar 6 actividades');
      }
      
      return addNewActivity(formFields);
    },
    [addNewActivity, activities, updateActivityQuantity]
  );

  const onPressBackButton = async () => {
    setApFormValues(propertyDataForm?.getFieldsValue());
    setCurrentStep(QuotationStep.ONE);
  };

  const onPressStepButtons = useCallback(async () => {
    try {
      const vigencyDate = propertyDataForm?.getFieldValue('vigencyDate');

      if (!vigencyDate[1]) {
        const quantityOfDays =
          propertyDataForm?.getFieldValue('quantityOfDays');
        const toDate = dayjs(vigencyDate[0]).add(quantityOfDays, 'days');
        propertyDataForm?.setFieldValue('vigencyDate', [
          vigencyDate[0],
          toDate,
        ]);
      }

      await propertyDataForm?.validateFields();

      setApFormValues(propertyDataForm?.getFieldsValue());

      if (activities.length > 0 && activities.length <= 6) {
        return propertyDataForm?.submit();
      }

      if (!activities.length) {
        return message.error('Debe agregar como mínimo una actividad');
      }

      if (activities.length > 6) {
        return message.error('Como máximo se pueden agregar 6 actividades');
      }
    } catch (error) {}
  }, [activities, propertyDataForm]);

  const getCategoryOfActivity = useCallback(
    (activityCode: string) => {
      const activity: APActivitiesPerProduct | undefined = apActiviesList.find(
        (el) => el.codigo === activityCode
      );
      return activity?.categoria;
    },
    [apActiviesList]
  );

  const onFinishPropertyDataStep = useCallback(
    (formFields: StepTwoForm) => {
      setStepTwoForm(formFields);

      //* Format dates --> YYYYMMDD
      const vigencyFrom = dayjs(formFields.vigencyDate[0])
        .format('YYYY-MM-DD')
        .split('-')
        .join('');
      const vigencyTo = dayjs(formFields.vigencyDate[1])
        .format('YYYY-MM-DD')
        .split('-')
        .join('');

      //* Generate activities --> Activity[]
      const activitiesList: Array<Activity> = activities.map(
        ({ activity, quantity, relationship, ageRange }) => ({
          codigo: activity,
          cantidad: quantity,
          parentesco: relationship,
          secuencia: '1',
          categoria: getCategoryOfActivity(String(activity)) ?? '',
          rangoDeEdad: ageRange,
          coberturas: [],
        })
      );

      setQuotationForm((prevState) => ({
        ...prevState!,
        plan: formFields.plans,
        vigenciaDesde: vigencyFrom,
        vigenciaHasta: vigencyTo,
        cantidadActividades: String(activities.length),
        actividades: activitiesList,
      }));

      setCurrentStep(QuotationStep.THREE);
    },
    [
      setStepTwoForm,
      activities,
      setQuotationForm,
      getCategoryOfActivity,
      setCurrentStep,
    ]
  );

  const updateQuantityField = useCallback(() => {
    const vigencyField = propertyDataForm?.getFieldValue('vigencyDate');

    if (vigencyField) {
      const fromField = vigencyField[0];
      const toField = vigencyField[1];

      if (fromField && toField) {
        const quantityOfDays = dayjs(toField).diff(fromField, 'days');
        propertyDataForm?.setFieldValue('quantityOfDays', quantityOfDays);

        if (quotationInfo?.article) {
          const vigencyFrom = dayjs(fromField).format('YYYYMMDD');
          const vigencyTo = dayjs(toField).format('YYYYMMDD');
          getPaymentMethodPerVigency(quotationInfo.article, vigencyFrom, vigencyTo);
        }
      } else {
        propertyDataForm?.setFieldValue('quantityOfDays', 0);
      }
    } else {
      propertyDataForm?.setFieldValue('quantityOfDays', 0);
    }
    /* eslint-disable-next-line */
  }, [propertyDataForm?.getFieldValue('vigencyDate'), quotationInfo, getPaymentMethodPerVigency]);

  const updateDatePicker = (value: number | null): void => {
    if (value !== null) {
      const vigencyDateField = propertyDataForm?.getFieldValue('vigencyDate');
      let fromField = vigencyDateField?.[0] ?? dayjs();

      const updatedToField = dayjs(fromField).add(value, 'days');
      propertyDataForm?.setFieldValue('vigencyDate', [fromField, updatedToField]);
      propertyDataForm?.validateFields(['vigencyDate']);

      if (quotationInfo?.article) {
        const vigencyFrom = dayjs(fromField).format('YYYYMMDD');
        const vigencyTo = updatedToField.format('YYYYMMDD');
        getPaymentMethodPerVigency(quotationInfo.article, vigencyFrom, vigencyTo);
      }
    }
  };

  const clearFormsOnSelectProduct = () => {
    setStepTwoForm(undefined);
    setActivities([]);
    activitiesTableForm.resetFields();
  };

  const onConfirmChangeProduct = () => {
    setShowOnChangeProductModal(false);
    clearFormsOnSelectProduct();
    propertyDataForm?.setFieldValue('plans', productsSelectedHistory?.newValue);
    setPlanWaschanged(true);
  };

  const onCancelChangeProduct = () => {
    propertyDataForm?.setFieldValue(
      'plans',
      productsSelectedHistory?.prevValue
    );
    setShowOnChangeProductModal(false);
  };

  const { globalPublicConfigs } = useContext(MasterContext);
  const [explanatoryText, setExplanatoryText] = useState<string>("");    
  const handleExplanatoryText = () => {
    const text = getExplanatoryTexts(
        Number(quotationInfo?.branch),
        Number(quotationInfo?.article), 
        Number(productSelected),
      globalPublicConfigs?.QUOTATION_EXPLANATORY_TEXTS
    );
    setExplanatoryText(text);
  }; 

  const onSelectProduct = useCallback(
    async (product: string) => {
      try {
        if (!activities.length) {
          activitiesTableForm.resetFields();
          await loadActivitiesTableFormInfo(product);
        }
      } catch (error) {}
    },
    [activities, loadActivitiesTableFormInfo]
  );

  useEffect(() => {
    if (activitiesSelected?.length) setActivities(activitiesSelected);
  }, [activitiesSelected]);

  useEffect(() => {
    handleExplanatoryText();
    if (productSelected?.length) onSelectProduct(productSelected);
  }, [productSelected, activitySelected]);

  useEffect(() => {
    if (stepTwoForm) {
      for (let key in stepTwoForm) {
        propertyDataForm?.setFieldValue(key, stepTwoForm[key]);
      }
    }
    /* eslint-disable-next-line */
  }, []);

  useEffect(() => {
    if (apFormValues && Object.keys(apFormValues).length > 0) {
      propertyDataForm?.setFieldsValue(apFormValues);
    }
  }, [apFormValues, propertyDataForm]);

  return (
    <>
      <Form
        formProps={{
          form: propertyDataForm,
          name: 'property-data-form',
          onFinish: onFinishPropertyDataStep,
        }}
      >
        <ContentSection level='h3' title='Datos del bien'>
          <RowSize>
            <ColSize size={25}>
              <DatePickerForm
                formProps={{
                  initialValue: [dayjs(), dayjs().add(1, 'days')],
                  label: 'Vigencia',
                  name: 'vigencyDate',
                  rules: validateForm.generateRules({
                    label: 'vigencia',
                    rules: ['required'],
                  }),
                }}
                isRangePicker={true}
                rangePickerProps={{
                  minDate: dayjs(),
                  onChange: updateQuantityField,
                }}
              />
            </ColSize>

            <ColSize size={25}>
              <InputNumberForm
                formProps={{
                  initialValue: 1,
                  label: 'Cantidad de días:',
                  name: 'quantityOfDays',
                }}
                inputNumberProps={{
                  controls: {
                    upIcon: <PlusOutlined />,
                    downIcon: <MinusOutlined />,
                  },
                  min: 1,
                  onChange: (value) => updateDatePicker(Number(value)),
                  placeholder: '1',
                }}
              />
            </ColSize>
          </RowSize>
        </ContentSection>

        <ContentSection level='h3' title='Planes'>
          <RowSize>
            <ColSize size={25}>
              <SelectForm
                formProps={{
                  label: 'Lista de planes:',
                  name: 'plans',
                  normalize: checkProductChange,
                  rules: validateForm.generateRules({
                    label: 'lista de planes',
                    rules: ['required'],
                  }),
                }}
                selectProps={{
                  loading: isLoadingProductsList,
                  onSelect: onSelectProduct,
                  onChange: handleExplanatoryText,
                  options: productsList,
                }}
              />
            </ColSize>
          </RowSize>
          {!!explanatoryText && 
                <Typography.Text strong style={{ fontSize: 16, color: COLORS.neutrals[70] }} >
                  {explanatoryText}
                </Typography.Text>
          }          
        </ContentSection>
      </Form>

      <Form
        formProps={{
          form: activitiesTableForm,
          name: 'activities-form',
          onFinish: onPressAddActivityButton,
        }}
      >
        <ContentSection level='h3' title='Actividades'>
          <RowSize>
            <ColSize size={25}>
              <SelectForm
                formProps={{
                  label: 'Actividad:',
                  name: 'activity',
                  rules: validateForm.generateRules({
                    label: 'actividad',
                    rules: ['required'],
                  }),
                }}
                selectProps={{
                  disabled:
                    isLoadingProductsInfo || !apActiviesListOptions.length,
                  loading: isLoadingProductsInfo,
                  options: apActiviesListOptions,
                  showSearch: true,
                }}
              />
            </ColSize>

            <ColSize size={25}>
              <InputNumberForm
                formProps={{
                  initialValue: 1,
                  label: 'Cantidad: (Máximo 99 Personas)',
                  name: 'quantity',
                  rules: validateForm.generateRules({
                    label: 'cantidad',
                    rules: ['required', 'max:99',],
                  }),
                }}
                inputNumberProps={{
                  controls: {
                    upIcon: <PlusOutlined />,
                    downIcon: <MinusOutlined />,
                  },
                  min: 1,
                  placeholder: '1',
                }}
              />
            </ColSize>

            <ColSize size={25}>
              <SelectForm
                formProps={{
                  label: 'Rango de Edad:',
                  name: 'ageRange',
                  rules: validateForm.generateRules({
                    label: 'rango de edad',
                    rules: ['required', 'max:99',],
                  }),
                }}
                selectProps={{
                  disabled: isLoadingProductsInfo || !ageRangeList.length,
                  loading: isLoadingProductsInfo,
                  options: ageRangeList,
                }}
              />
            </ColSize>

            <ColSize size={25}>
              <SelectForm
                formProps={{
                  label: 'Parentesco:',
                  name: 'relationship',
                  rules: validateForm.generateRules({
                    label: 'parentesco',
                    rules: ['required'],
                  }),
                }}
                selectProps={{
                  disabled: isLoadingProductsInfo || !relationshipList.length,
                  loading: isLoadingProductsInfo,
                  options: relationshipList,
                }}
              />
            </ColSize>

            <ColSize size={25}>
              <FormAntd.Item label=' '>
                <ActionButton
                  className='btn-accent-outline-barbuss'
                  icon={<PlusOutlined />}
                  label='Agregar'
                  htmlType='submit'
                  type='secondary'
                  style={{ width: 165, height: 32 }}
                />
              </FormAntd.Item>
            </ColSize>
          </RowSize>

          <Table
            bordered
            className='mt-1 person-table'
            columns={MOCK_TABLE_COLUMNS_HEADER}
            dataSource={activities}
            pagination={false}
            scroll={{ x: 'auto' }}
          />
        </ContentSection>
      </Form>

      <QuotationStepButtons
        onPressBackButton={onPressBackButton}
        onClick={onPressStepButtons}
      />

      <Modal
        centered
        open={showOnChangeProductModal}
        width={650}
        title='¿Está seguro que desea cambiar el plan seleccionado?'
        footer
        onCancel={onCancelChangeProduct}
        onOk={onConfirmChangeProduct}
      >
        <Row align='middle' justify='end' style={{ marginBottom: 15 }}>
          <Col span={24}>
            <Typography.Text>
              Si cambia el plan seleccionado previamente perderá todo el listado
              de actividades que haya preseleccionado, ¿Está seguro que desea
              continuar?
            </Typography.Text>
          </Col>
        </Row>
      </Modal>
    </>
  );
};
